// Web Push این دستگاه (AP-MSG-02، US-MSG-002، AC-MSG-002.2): ثبت Service Worker (`/sw.js`)، اجازه اعلان مرورگر، Subscription با کلید
// عمومی VAPID سرور و متن‌های بومی‌سازی‌شده نمایش (Service Worker متن پیام را هرگز نمی‌گیرد — BR-MSG-003). بدون هیچ ذخیره در localStorage.
import { createContext, useContext } from 'react';
import { SERVICE_WORKER_URL } from '../../offline/serviceWorker';

/** متن‌های نمایش اعلان در Service Worker (از i18n). */
export interface PushTexts {
  title: string;
  body: string;
  critical: string;
  message: string;
}

/** Subscription قابل ارسال به API (`PushSubscription.toJSON()`). */
export interface PushSubscriptionJson {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** دسترسی به Push مرورگر (قابل جایگزینی در تست). */
export interface PushDevice {
  /** مرورگر Service Worker، PushManager و Notification دارد. */
  supported: () => boolean;
  /** وضعیت اجازه اعلان. */
  permission: () => NotificationPermission;
  /** Subscription فعلی این دستگاه یا تهی. */
  current: () => Promise<PushSubscriptionJson | null>;
  /** درخواست اجازه، ثبت Service Worker و Subscription تازه. */
  subscribe: (publicKey: string, texts: PushTexts) => Promise<PushSubscriptionJson>;
  /** لغو Subscription این دستگاه؛ نشانی لغوشده یا تهی. */
  unsubscribe: () => Promise<string | null>;
}

/** کلید Base64Url ← بایت‌ها (applicationServerKey). */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function toJson(subscription: PushSubscription): PushSubscriptionJson {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}

async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration('/');
}

/** پیاده‌سازی مرورگر. */
export const browserPushDevice: PushDevice = {
  supported: () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window,
  permission: () => (typeof Notification === 'undefined' ? 'denied' : Notification.permission),
  current: async () => {
    const existing = await registration();
    const subscription = await existing?.pushManager.getSubscription();
    return subscription ? toJson(subscription) : null;
  },
  subscribe: async (publicKey, texts) => {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('push_denied');
    }
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
    const ready = await navigator.serviceWorker.ready;
    ready.active?.postMessage({ type: 'phx-push-texts', texts });
    const subscription =
      (await ready.pushManager.getSubscription()) ??
      (await ready.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToBytes(publicKey),
      }));
    return toJson(subscription);
  },
  unsubscribe: async () => {
    const existing = await registration();
    const subscription = await existing?.pushManager.getSubscription();
    if (!subscription) {
      return null;
    }
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    return endpoint;
  },
};

export const PushDeviceContext = createContext<PushDevice>(browserPushDevice);

export function usePushDevice(): PushDevice {
  return useContext(PushDeviceContext);
}
